import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router';

const scrollPositions = new Map<string, number>();

// Disable automatic scroll restoration to manage it manually
try {
  if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
  }
} catch {
  /* empty */
}

export const useScrollPosition = (
  containerRef: React.RefObject<HTMLElement>,
) => {
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const intervalRef = useRef<number | null>(null);

  const storageKey = `scroll:${currentPath}`;

  // Save scroll position
  // fires after each render when currentPath or containerref changes
  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const scrollTop = containerRef.current.scrollTop;
        scrollPositions.set(currentPath, scrollTop);
        try {
          sessionStorage.setItem(storageKey, String(scrollTop));
        } catch {
          /* empty */
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    // Clean up
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [currentPath, containerRef]);

  // Restore scroll position before the browser paints
  // useLayoutEffect fires when the scroll component re-renders
  useLayoutEffect(() => {
    // read saved position from memory or sessionStorage
    let savedPosition = scrollPositions.get(currentPath);
    if (savedPosition === undefined) {
      try {
        const fromStorage = sessionStorage.getItem(storageKey);
        if (fromStorage !== null) savedPosition = Number(fromStorage);
      } catch {
        /* empty */
      }
    }

    // nothing to restore
    if (savedPosition === undefined || savedPosition === 0) return;

    // if content is not tall enough to scroll, wait until it is
    const el = containerRef.current;
    if (!el) return;

    // restore immediately if content is already tall enough
    if (el.scrollHeight - el.clientHeight >= savedPosition) {
      el.scrollTop = savedPosition;
      return;
    }

    // poll every 50ms until content is tall enough to restore
    const timer = window.setTimeout(() => {
      window.clearInterval(intervalRef.current!);
      intervalRef.current = null;
    }, 5000);

    // set up interval to check if content is tall enough
    intervalRef.current = window.setInterval(() => {
      if (el.scrollHeight - el.clientHeight >= savedPosition!) {
        el.scrollTop = savedPosition!;
        window.clearInterval(intervalRef.current!);
        window.clearTimeout(timer);
        intervalRef.current = null;
      }
    }, 50);

    return () => {
      window.clearInterval(intervalRef.current!);
      window.clearTimeout(timer);
      intervalRef.current = null;
    };
  }, [currentPath, containerRef]);
};
